import type { ReactNode } from 'react'
import {
  formatKopecks,
  formatPerGram,
  formatPercent,
  formatRub,
  signClass,
  type UnitTotals,
} from '@/lib/unit-economics'

function formatAverage(value: number | null): string {
  return formatPerGram(value)
}

function Card({ label, value, detail, tone, title, testId }: {
  label: string
  value: string
  detail?: ReactNode
  tone?: string
  title?: string
  testId: string
}) {
  return (
    <div data-testid={testId} title={title} className="flex min-w-0 flex-col gap-1 border border-rule bg-paper px-3.5 py-3 text-left">
      <span className="text-[11px] uppercase tracking-wide text-ink-muted">{label}</span>
      <span className={`tab-nums whitespace-nowrap text-[18px] font-semibold leading-tight ${tone ?? 'text-ink'}`}>{value}</span>
      {detail ? <span className="text-[11px] text-ink-faint">{detail}</span> : null}
    </div>
  )
}

function MetalDetail({ values }: { values: UnitTotals['soldForPerGram'] }) {
  return <span className="flex flex-col gap-0.5"><span className="whitespace-nowrap">золото {formatAverage(values.gold)}</span><span className="whitespace-nowrap">серебро {formatAverage(values.silver)}</span></span>
}

export function UnitSummaryCards({ totals, costMissing, onCostMissing }: {
  totals: UnitTotals
  costMissing: boolean
  onCostMissing: () => void
}) {
  return (
    <div className="flex flex-col gap-2">
      <div data-testid="unit-cards" className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-10">
        <Card testId="unit-card-bought" label="Купили за" value={formatRub(totals.bought)} />
        <Card testId="unit-card-sold-for" label="Продали за" value={formatRub(totals.soldFor)} detail="деньги до удержаний" />
        <Card testId="unit-card-margin" label="Маржа" value={formatRub(totals.margin)} tone={signClass(totals.margin)}
          detail={`${formatPercent(totals.marginOfCost)} от закупки`} title={`${formatPercent(totals.marginOfSale)} от цены продажи`} />
        <Card testId="unit-card-sold-for-per-gram" label="Продали за, ₽/г" value=""
          detail={<MetalDetail values={totals.soldForPerGram} />} />
        <Card testId="unit-card-cost-per-gram" label="Купили за, ₽/г" value=""
          detail={<MetalDetail values={totals.costPerGram} />} />
        <Card testId="unit-card-profit" label="Прибыль" value={formatKopecks(totals.profit)} tone={signClass(totals.profit)}
          detail={`${formatPercent(totals.profitRate)} от продаж · в среднем ${totals.averageProfit === null ? '—' : formatKopecks(totals.averageProfit)} с изделия`} />
        <Card testId="unit-card-market-fee-rate" label="Удержания Маркета" value={formatPercent(totals.marketFeeRate)}
          detail="% от продаж" />
        <Card testId="unit-card-average-check" label="Средний чек" value={totals.averageCheck === null ? '—' : formatKopecks(totals.averageCheck)} />
        <Card testId="unit-card-sold" label="Продано" value={totals.sold.toLocaleString('ru-RU')} />
      </div>
      {costMissing && totals.costReasons.length > 0 ? (
        <button type="button" data-testid="unit-cost-reasons" onClick={onCostMissing} className="flex flex-wrap gap-x-4 text-left text-[12px] text-ink-2">
          {totals.costReasons.map((item) => <span key={item.reason}>{item.reason} — {item.count}</span>)}
        </button>
      ) : null}
    </div>
  )
}
